import { ref } from '@vue/composition-api';

import { useMessages } from '@/base/app';
import {
  BaseDialogFullScreen,
  BaseDialogFullScreenValue,
} from '@/base/app/components/molecules/BaseDialogFullScreenComposable';
import { clearDialogQuery } from '@/base/app/utils/DialogQueryUtils';
import { GroupId, UserId, UserName } from '@/base/domains';
import { isFailed } from '@/base/usecases';
import { assertIsDefined } from '@/utils/Asserts';
import { useRoute, useRouter } from '@/utils/VueUtils';

import { UserExamCorrections } from '../../../domains';
import { useGetUserExamCorrections } from '../../../usecases';

function useDialogFullScreen() {
  const dialogFullScreen = ref<BaseDialogFullScreen>();
  const dialog = ref<BaseDialogFullScreenValue>({ display: false });
  function opened() {
    return dialog.value.display;
  }
  function info(msg: string, ok?: () => void) {
    assertIsDefined(dialogFullScreen.value);
    dialogFullScreen.value.showDialog(msg, ok);
  }
  return { dialogFullScreen, dialog, opened, info };
}

export function useUserUsageGroupExamDialog() {
  const msgs = useMessages({ prefix: 'report.organisms.userUsageGroupExamDialog' });
  const route = useRoute();
  const router = useRouter();
  const { dialogFullScreen, dialog, opened, info } = useDialogFullScreen();

  const reports = ref<UserExamCorrections[]>([]);
  const userName = ref<UserName>('');
  const loading = ref(false);

  const getReports = useGetUserExamCorrections();
  async function fetch(groupId: GroupId, userId: UserId) {
    loading.value = true;
    const res = await getReports.execute({ groupId, userId });
    loading.value = false;
    if (isFailed(res)) return false;
    reports.value = res.examResults;
    userName.value = res.userName;
    return true;
  }

  function close() {
    dialog.value = { display: false };
    reports.value = [];
    userName.value = '';
    const to = clearDialogQuery(route, '#userUsageTraineeTable');
    if (to) router.replace(to);
  }

  async function open(groupId: GroupId, userId: UserId) {
    dialog.value = { display: true };
    const ret = await fetch(groupId, userId);
    if (!ret) info(msgs.of('noData').value, close);
  }

  return {
    dialogFullScreen,
    dialog,
    loading,
    items: reports,
    title: msgs.of('userUsageExam'),
    subtitle: userName,
    close,
    open,
    opened,
  };
}

export type UserUsageGroupExamDialog = ReturnType<typeof useUserUsageGroupExamDialog>;
